/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { useMemo } from 'react';
import { Box, Button, Flex, HStack, Input, InputGroup, InputLeftElement, Spinner, Text, useDisclosure } from '@chakra-ui/core';
import { SearchIcon } from '@chakra-ui/icons';
import { MdFilterList } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import debounce from 'lodash/debounce';
import { IoMdRefresh } from 'react-icons/io';
import { useHistory, useLocation, useParams } from 'react-router';
import colors from '../../../styles/colors';
import { SelectedTab, useTopCandidatesContext } from './TopCandidatesContext';
import { TopCandidateDataType } from './TopCandidateDataType';
import { CandidateView } from './CandidateView';
import { ContactRequestModalView } from './ContactRequestModalView';
import { PATH_POSITION_TABS, PositionTabParams } from '../../../routes/constants';
import { TemplatePagination } from '../languageEvaluation/languageEvaluationTemplate/TemplatePagination';

export type CandidatesListPros = {
  isLoading: boolean;
  topCandidates: TopCandidateDataType[];
  setIsRefreshClicked: (isClicked: boolean) => void;
  hasNextData: boolean;
  hasPreviousData: boolean;
  handleNextData: () => void;
  handlePrevData: () => void;
  totalCount: number | undefined;
  currentCount: number;
  isFetching?: boolean;
  onSearch: (searchTerm: string) => void;
};

export const CandidatesList = ({
  isLoading,
  topCandidates,
  setIsRefreshClicked,
  hasNextData,
  hasPreviousData,
  handleNextData,
  handlePrevData,
  totalCount,
  currentCount,
  isFetching,
  onSearch,
}: CandidatesListPros): JSX.Element => {
  const { positionId, tab: currentTabFromParams } = useParams<PositionTabParams>();
  const { t } = useTranslation('businessManagement');
  const history = useHistory();
  const location = useLocation();

  const fromPage = location.state?.fromPage;

  const {
    isFilterOpened,
    setIsFilterOpened,
    tab,
    setTab,
    selectedTopCandidate,
    setSelectedTopCandidate,
    filterParams,
    positionDocument,
  } = useTopCandidatesContext();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchTerm, setSearchTerm] = React.useState<string>('');
  const [inputValue, setInputValue] = React.useState<string>('');

  React.useEffect(() => {
    if (currentTabFromParams) {
      setTab(currentTabFromParams as SelectedTab);
    }
  }, [setTab, currentTabFromParams]);

  // get filtered candidates
  const getFilteredCandidate = (givenCandidates: TopCandidateDataType[]): TopCandidateDataType[] => {
    const candidates = givenCandidates.filter((c) => {
      const names = [c.seeker.firstName, c.seeker.lastName];
      return searchTerm === '' || names.some((name) => name.toLowerCase().includes(searchTerm.toLowerCase()));
    });

    let reformattedData = candidates;

    // let reformattedData = candidates.map((c) => ({
    //   ...c,
    //   withinRange: c.withinRange ? 'no' : 'yes',
    // }));

    if (filterParams.salary) {
      reformattedData = reformattedData.filter((c) => c.withinRange === filterParams?.salary);
    }
    if (filterParams.salaryRange && !(filterParams.salaryRange.from === 0 && filterParams.salaryRange.to === 0)) {
      reformattedData = reformattedData.filter(
        (c) =>
          c.seeker.expectedSalary &&
          filterParams.salaryRange &&
          parseFloat(c.seeker.expectedSalary.value) >= filterParams?.salaryRange.from &&
          parseFloat(c.seeker.expectedSalary.value) <= filterParams?.salaryRange.to,
      );
    }
    // if (filterParams.otherFilterParams?.includes('hasFlexibleTransportation')) {
    //   reformattedData = reformattedData.filter((c) => c.hasFlexibleTransportation);
    // }
    if (filterParams.flexibleTransportation) {
      reformattedData = reformattedData.filter((c) => c.hasFlexibleTransportation);
    }
    if (filterParams.experienceInYears) {
      reformattedData = reformattedData.filter(
        (c) => filterParams.experienceInYears && c.workExperience >= filterParams?.experienceInYears,
      );
    }

    // Filter by last active
    if (filterParams.lastActivityInDays) {
      const now = moment();
      reformattedData = reformattedData.filter((c) => {
        const lastActive = moment(c.seeker.lastActivity);
        const diffInDays = now.diff(lastActive, 'days');

        return filterParams.lastActivityInDays && diffInDays <= filterParams?.lastActivityInDays;
      });
    }

    return reformattedData;
  };

  const handleContactRequest = (candidate: TopCandidateDataType) => {
    setSelectedTopCandidate(candidate);
    onOpen();
  };

  const getNoCandidatesMessage = (selectedTab: SelectedTab) => {
    if (selectedTab === SelectedTab.BEST_MATCH) {
      return t('topCandidate.noTopCandidates');
    }
    if (selectedTab === SelectedTab.BOOKMARKED) {
      return t('topCandidate.noBookmarkedCandidates');
    }
    if (selectedTab === SelectedTab.TO_REVIEW) {
      return t('topCandidate.noReviewCandidates');
    }
    return t('topCandidate.noActiveCandidates');
  };

  const getPaginationText = (selectedTab: SelectedTab, variable: string) => {
    if (selectedTab === SelectedTab.BOOKMARKED) {
      return t('topCandidate.pagination.showingBookmarkedResults', variable);
    }
    if (selectedTab === SelectedTab.TO_REVIEW) {
      return t('topCandidate.pagination.showingReviewResults', variable);
    }
    return t('topCandidate.pagination.showingActiveResults', variable);
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        if (tab === SelectedTab.BEST_MATCH) {
          setSearchTerm(value);
        } else {
          onSearch(value);
        }
      }, 500),
    [tab, setSearchTerm, onSearch],
  );

  // Clean up debounce on unmount
  React.useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // KeyDown handler
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      debouncedSearch(inputValue);
    }
  };

  return (
    <Box height="100%" position="absolute" w="100%" display="flex" flexDirection="column">
      <Box flexShrink={0}>
        <HStack justify="space-between" align="center" borderBottom="1px solid #E5E7EB" px={3} py={2}>
          <Flex alignItems="center" flexGrow={1}>
            {!isFilterOpened && (
              <Box borderRight="1px solid #E5E7EB" p="6px 18px" mr={3}>
                <Flex
                  alignItems="center"
                  borderRadius="30px"
                  background={colors.neutral[100]}
                  p="6px"
                  _hover={{ backgroundColor: colors.neutral[200] }}
                  onClick={() => setIsFilterOpened(true)}
                  cursor="pointer"
                >
                  <MdFilterList fontSize="24px" />
                  <Text fontSize="18px" fontWeight={700} ml="8px">
                    {t('topCandidate.filters')}
                  </Text>
                </Flex>
              </Box>
            )}

            <Flex direction="column">
              <Text fontSize="xs" textTransform="uppercase">
                {positionDocument?.businessName}
              </Text>
              <Text color={colors.blue.default} fontSize="18px" fontWeight={700}>
                {positionDocument?.customName}
              </Text>
            </Flex>
          </Flex>
          <InputGroup flexBasis="40%" flexShrink={0}>
            <InputLeftElement pointerEvents="none">
              <SearchIcon />
            </InputLeftElement>
            <Input
              borderRadius="30px"
              data-testid="searchInput"
              type="text"
              value={inputValue}
              placeholder={t('topCandidate.searchCandidates')}
              onChange={(e) => {
                const trimmedName = e.target.value
                  .replace(/[^a-zA-Z0-9\s.]/g, '')
                  .replace(/\s+/g, ' ')
                  .trimStart();
                setInputValue(trimmedName);
              }}
              onKeyDown={handleKeyDown}
            />
          </InputGroup>
        </HStack>
        <HStack justify="space-between" align="center" borderBottom="1px solid #E5E7EB" px={3} py={2}>
          <Button
            onClick={() => setIsRefreshClicked(true)}
            size="md"
            fontSize="sm"
            colorScheme="gray"
            leftIcon={<IoMdRefresh size={20} />}
            data-testid="refreshBtn"
            mr={4}
            variant="outline"
          >
            {t('jobPosition:refresh')}
          </Button>
          <Box>
            {[
              { label: t('topCandidate.top'), type: SelectedTab.BEST_MATCH },
              { label: t('topCandidate.bookmarked'), type: SelectedTab.BOOKMARKED },
              { label: t('topCandidate.review'), type: SelectedTab.TO_REVIEW },
              { label: t('topCandidate.active'), type: SelectedTab.ACTIVE },
            ].map((btnTab, index) => (
              <Button
                key={btnTab.type}
                border="1px solid"
                borderColor={colors.neutral[200]}
                variant="outline"
                fontSize="sm"
                _focus={{
                  boxShadow: 'none',
                }}
                minW="8rem"
                background={tab === btnTab.type ? '#263DBA1A' : colors.neutral[100]}
                onClick={() =>
                  history.push(
                    {
                      pathname: PATH_POSITION_TABS(positionId, btnTab.type),
                    },
                    {
                      fromPage,
                    },
                  )
                }
                borderTopRightRadius={index === 3 ? '5px' : '0'}
                borderBottomRightRadius={index === 3 ? '5px' : '0'}
                borderTopLeftRadius={index === 0 ? '5px' : '0'}
                borderBottomLeftRadius={index === 0 ? '5px' : '0'}
              >
                {btnTab.label}
              </Button>
            ))}
          </Box>
        </HStack>
      </Box>
      <Box flex="1" overflowY="auto" mt={2}>
        {isLoading ? (
          <Flex alignItems="center" justifyContent="center" h="100px">
            <Spinner size="md" colorScheme="blue.500" />
          </Flex>
        ) : (
          getFilteredCandidate(topCandidates).map((topCandidate) => (
            <CandidateView key={topCandidate.seeker.id} topCandidate={topCandidate} handleContactRequest={handleContactRequest} />
          ))
        )}

        {!isLoading && tab !== SelectedTab.BEST_MATCH && topCandidates.length > 0 && (
          <Box>
            <TemplatePagination
              hasNext={hasNextData}
              hasPrevious={hasPreviousData}
              isFetching={isFetching}
              handleNextPage={handleNextData}
              handlePreviousPage={handlePrevData}
              totalDataCount={totalCount}
              dataCount={currentCount}
              showingResultsTranslation={(variable: string) => getPaginationText(tab, variable)}
            />
          </Box>
        )}

        {!isLoading && topCandidates.length <= 0 && (
          <Flex minH="200px" alignItems="center" justifyContent="center">
            <Text fontSize="lg" fontWeight="bold">
              {getNoCandidatesMessage(tab)}
            </Text>
          </Flex>
        )}
        {isOpen && selectedTopCandidate && (
          <ContactRequestModalView isOpen={isOpen} onClose={onClose} selectedTopCandidate={selectedTopCandidate} />
        )}
        <Box h="30px" />
      </Box>
    </Box>
  );
};
